import { createContext, useContext, useState, useMemo, useCallback, useEffect } from 'react';
import { useCheckit } from './CheckitAuthContext';
import { detectGeo } from '@/lib/geo';

const KEY = 'checkit:currency';
const ZERO_DECIMAL = new Set(['NGN', 'KES', 'ZAR', 'GHS']);
const FALLBACK_CURRENCIES = [{ code: 'USD', symbol: '$', label: 'US Dollar' }];
const FALLBACK_RATES = { USD: 1 };

const Ctx = createContext(null);

export function CheckitCurrencyProvider({ children }) {
  const { config } = useCheckit();
  const currencies = useMemo(
    () => (config?.currencies?.length ? config.currencies : FALLBACK_CURRENCIES),
    [config]);
  const rates = useMemo(() => config?.fx?.rates || FALLBACK_RATES, [config]);

  const [stored, setStored] = useState(() => {
    try { return localStorage.getItem(KEY) || 'USD'; } catch { return 'USD'; }
  });

  const setCurrency = useCallback((c) => {
    setStored(c);
    try { localStorage.setItem(KEY, c); } catch { /* ignore */ }
  }, []);

  // First visit (no explicit choice): default currency to the device's location.
  useEffect(() => {
    let chosen = false;
    try { chosen = !!localStorage.getItem(KEY); } catch { /* ignore */ }
    if (chosen || !currencies.length) return;
    detectGeo().then((g) => {
      const cur = g?.currency;
      if (cur && currencies.some((c) => c.code === cur)) setCurrency(cur);
    });
  }, [currencies, setCurrency]);

  const active = currencies.some((c) => c.code === stored) ? stored : 'USD';
  const symbol = (currencies.find((c) => c.code === active) || {}).symbol || '$';
  const rate = Number(rates[active] || 1);

  const convert = useCallback((usd) => {
    const amt = Number(usd || 0) * rate;
    return ZERO_DECIMAL.has(active) ? Math.round(amt) : Math.round(amt * 100) / 100;
  }, [rate, active]);

  const format = useCallback((usd) => {
    const amt = convert(usd);
    const opts = ZERO_DECIMAL.has(active)
      ? { maximumFractionDigits: 0 }
      : { minimumFractionDigits: amt % 1 === 0 ? 0 : 2, maximumFractionDigits: 2 };
    return `${symbol}${amt.toLocaleString('en-US', opts)}`;
  }, [convert, symbol, active]);

  const value = useMemo(() => ({
    currency: active, setCurrency, currencies, rates, symbol, rate, convert, format,
  }), [active, setCurrency, currencies, rates, symbol, rate, convert, format]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useCheckitCurrency = () => useContext(Ctx) || {
  currency: 'USD', currencies: [], symbol: '$', rate: 1,
  setCurrency: () => {}, convert: (n) => n, format: (n) => `$${n}`,
};
